import React, { useState } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import ReplayOutlined from '@mui/icons-material/ReplayOutlined'
import { MdArrowBack } from 'react-icons/md'
import toast from 'react-hot-toast'
import AppButton from '../../shared/AppButton'
import AppModal from '../../components/AppModal'
import AppTable from '../../shared/AppTable'
import Pagination from '../../components/Pagination'
import TableSkeletonLoader from '../../common/TableLoading'
import claims from '../../gql/claims'

const getMessage = error =>
  error?.graphQLErrors?.[0]?.message ||
  error?.message ||
  'Something went wrong.'

function SummaryCard ({ label, value }) {
  return (
    <div className='rounded-md border border-slate-200 bg-slate-50 px-3 py-2'>
      <p className='text-[10px] font-medium uppercase tracking-wide text-slate-500'>
        {label}
      </p>
      <p className='mt-1 text-base font-semibold text-slate-800'>
        {value ?? '—'}
      </p>
    </div>
  )
}

export default function ClaimReferralBulkJobDetails () {
  const { i18n } = useTranslation()
  const isRTL = i18n.dir() === 'rtl'
  const navigate = useNavigate()
  const { jobId } = useParams()
  const [page, setPage] = useState(1)
  const [confirmRevert, setConfirmRevert] = useState(false)
  const limit = 10

  const { data, loading, refetch } = useQuery(
    claims.GET_CLAIM_REFERRAL_BULK_LOG_DETAILS,
    {
      variables: { jobId, page, limit },
      fetchPolicy: 'network-only',
      onError: error => toast.error(getMessage(error))
    }
  )

  const [revertJob, { loading: reverting }] = useMutation(
    claims.REVERT_CLAIM_REFERRAL_BULK_UPDATE,
    {
      onCompleted: payload => {
        toast.success(
          payload?.revertClaimReferralBulkUpdate?.message ||
            'Referral bulk update revert started.'
        )
        setConfirmRevert(false)
        refetch()
      },
      onError: error => toast.error(getMessage(error))
    }
  )

  const details = data?.getClaimReferralBulkLogDetails
  const job = details?.job || {}
  const rows = details?.data || []
  const pages = details?.pages || 1
  const canRevert =
    job?.status === 'completed' && job?.job_type !== 'revert' && !job?.reverted

  const columns = [
    { header: 'Transaction ID', field: 'transaction_id' },
    { header: 'Request Bundle ID', field: 'request_bundle_id' },
    { header: 'Patient', field: 'patient_name' },
    { header: 'Document ID', field: 'patient_document_id' },
    {
      header: 'Old Referral',
      render: row => row.old_pre_auth_ref_no || '—'
    },
    {
      header: 'New Referral',
      render: row => row.new_pre_auth_ref_no || '—'
    },
    {
      header: 'Referral In',
      render: row =>
        `${row.old_is_referral_in ? 'Yes' : 'No'} → ${
          row.new_is_referral_in ? 'Yes' : 'No'
        }`
    },
    { header: 'Outcome', field: 'outcome_status' },
    { header: 'Reverted', render: row => (row.reverted ? 'Yes' : 'No') }
  ]

  return (
    <div className='w-full'>
      <div className='mb-2 flex items-center justify-between gap-2'>
        <div className='flex items-center gap-2'>
          <div
            onClick={() => navigate('/claims/referral-bulk-update')}
            className='bg-[#0070C2] hover:bg-[#0070C2]/80 cursor-pointer rounded-full p-1'
            title='Back to referral update logs'
          >
            <MdArrowBack
              className={
                isRTL
                  ? 'text-white h-4 w-4 rotate-180'
                  : 'text-white h-4 w-4'
              }
            />
          </div>
          <div>
            <h1 className='text-lg font-semibold text-black/70'>
              Referral Update Job Details
            </h1>
            <p className='text-[11px] text-slate-500'>
              Job ID: <span className='font-mono'>{jobId}</span>
            </p>
          </div>
        </div>
        {canRevert && (
          <AppButton
            label='Revert'
            bgColor='#dc2626'
            startIcon={<ReplayOutlined />}
            onClick={() => setConfirmRevert(true)}
          />
        )}
      </div>

      <div className='mb-2 grid grid-cols-2 gap-2 md:grid-cols-4 xl:grid-cols-5'>
        <SummaryCard label='Status' value={job.status} />
        <SummaryCard label='Outcome' value={job.outcome_status} />
        <SummaryCard label='Claims Updated' value={job.total_claims_updated || 0} />
        <SummaryCard label='Rows' value={details?.total || 0} />
      </div>

      <AppTable
        columns={columns}
        data={rows}
        loading={loading}
        actions={false}
        bodyRowPadding='7px'
        LoaderComponent={<TableSkeletonLoader columnCount={9} rowCount={8} />}
      />

      <div className='mt-2'>
        <Pagination
          currentPage={page}
          totalPages={pages}
          onPageChange={newPage => setPage(newPage)}
        />
      </div>

      <AppModal
        open={confirmRevert}
        onClose={() => !reverting && setConfirmRevert(false)}
        title='Revert referral update'
        maxWidth='sm'
      >
        <p className='text-sm text-slate-600'>
          This will revert all referral updates made by job{' '}
          <span className='font-mono text-xs'>{jobId}</span>.
        </p>
        <div className='mt-5 flex justify-end gap-2'>
          <AppButton
            label='Cancel'
            variant='outlined'
            bgColor='transparent'
            onClick={() => setConfirmRevert(false)}
            disabled={reverting}
          />
          <AppButton
            label='Revert changes'
            bgColor='#dc2626'
            onClick={() =>
              revertJob({
                variables: {
                  input: {
                    job_id: jobId,
                    batch_size: 200
                  }
                }
              })
            }
            isLoading={reverting}
            loadingText='Reverting...'
          />
        </div>
      </AppModal>
    </div>
  )
}
